import { useRef, useState } from "react";
import { BookOpenText, ChevronLeft, RotateCw, Search, SearchX, XCircle } from "lucide-react";
import grammar from "@/data/grammar.json";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const normalize = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\u064B-\u0652\u0640]/g, "")
    .trim();

const GRAMMAR_TOTAL = 72;

const RuleCard = ({ rule, level, index }) => (
  <article
    data-testid={`grammar-rule-card-${index}`}
    className="flex h-full flex-col rounded-2xl border border-navy-900/10 bg-white p-5 transition duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-navy-900/5"
    style={{ borderTop: `4px solid ${level.color}` }}
  >
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 data-testid={`grammar-rule-name-ar-${index}`} className="text-base font-bold leading-snug">{rule.nameAr}</h3>
        <p dir="ltr" className="mt-1 text-left font-en text-sm font-semibold text-navy-900/55">{rule.nameEn}</p>
      </div>
      <span
        className="shrink-0 rounded-full px-2.5 py-1 font-en text-[11px] font-bold"
        style={{ color: level.color, backgroundColor: `${level.color}1f` }}
      >
        {level.key}
      </span>
    </div>

    {rule.category && (
      <span className="mb-3 inline-flex w-fit items-center rounded-full bg-navy-900/6 px-3 py-1 text-[11px] font-semibold text-navy-900/65">
        {rule.category}
      </span>
    )}

    {rule.formula && (
      <div dir="ltr" className="mb-3 rounded-lg bg-teal-500/8 p-3 text-left">
        <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-teal-700">Formula</p>
        <p className="font-serifen text-sm text-navy-900">{rule.formula}</p>
      </div>
    )}

    {rule.explanation && <p className="mb-3 text-sm leading-loose text-navy-900/80">{rule.explanation}</p>}

    {rule.example && (
      <div className="mb-3 flex-1">
        <p dir="ltr" className="border-l-[3px] border-gold pl-3 text-left font-serifen text-sm leading-relaxed text-[#26324B]">
          {rule.example}
        </p>
        {rule.exampleAr && <p className="mt-1.5 text-sm text-navy-900/60">{rule.exampleAr}</p>}
      </div>
    )}

    {rule.mistake && (
      <div className="mt-auto flex items-start gap-2 rounded-lg bg-rose-500/8 p-3" data-testid={`grammar-rule-mistake-${index}`}>
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
        <p className="text-[13px] leading-relaxed text-rose-800">{rule.mistake}</p>
      </div>
    )}
  </article>
);

const GrammarPage = () => {
  const [activeKey, setActiveKey] = useState("A1");
  const [query, setQuery] = useState("");
  const touchRef = useRef(null);

  const level = grammar.levels.find((l) => l.key === activeKey) || grammar.levels[0];

  const filtered = level.rules.filter((r) => {
    if (!query.trim()) return true;
    const q = normalize(query);
    return [r.nameAr, r.nameEn, r.category, r.explanation, r.example, r.exampleAr, r.mistake]
      .map(normalize)
      .some((f) => f.includes(q));
  });

  const totalRules = grammar.levels.reduce((n, l) => n + l.rules.length, 0);
  const isEmpty = totalRules === 0;

  const levelIndex = grammar.levels.findIndex((l) => l.key === activeKey);
  const step = (dir) => {
    const nextIdx = (levelIndex + dir + grammar.levels.length) % grammar.levels.length;
    setActiveKey(grammar.levels[nextIdx].key);
  };

  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    const dy = e.changedTouches[0].clientY - touchRef.current.y;
    touchRef.current = null;
    if (Math.abs(dx) > 64 && Math.abs(dy) < 48) {
      if (dx < 0) step(1);
      else step(-1);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-paper text-navy-900">
      <Navbar light />

      <header className="container-x pt-24 sm:pt-28">
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-teal-700">
          <BookOpenText className="h-4 w-4" />
          مرجعك الكامل للقواعد
        </p>
        <h1 data-testid="grammar-title" className="text-2xl font-bold leading-snug sm:text-4xl">
          دليل القواعد من A1 إلى C1
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-loose text-navy-900/65 sm:text-base">
          {GRAMMAR_TOTAL} قاعدة مشروحة بالعربية مع صيغها وأمثلتها الإنجليزية وترجماتها، والخطأ الشائع في كل قاعدة —
          ابحث فورياً أو تصفح حسب المستوى.
        </p>

        <div className="relative mt-7 max-w-xl">
          <Search className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-navy-900/40" />
          <input
            data-testid="grammar-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ابحث: اسم القاعدة، التصنيف، الشرح، أو المثال..."
            className="h-12 w-full rounded-2xl border border-navy-900/12 bg-white pr-12 pl-4 text-base outline-none transition placeholder:text-navy-900/35 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/15"
          />
          {query && (
            <button
              data-testid="grammar-search-clear"
              onClick={() => setQuery("")}
              aria-label="مسح البحث"
              className="absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-navy-900/6"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      <div className="sticky top-16 z-30 mt-7 border-y border-navy-900/8 bg-paper/92 backdrop-blur-lg">
        <div className="container-x flex items-center justify-between gap-3 py-3">
          <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
            {grammar.levels.map((l) => (
              <button
                key={l.key}
                data-testid={`grammar-tab-${l.key}`}
                onClick={() => setActiveKey(l.key)}
                className={`flex min-h-[44px] shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-bold transition ${
                  l.key === activeKey
                    ? "border-transparent text-navy-950"
                    : "border-navy-900/15 text-navy-900/65 hover:border-navy-900/35"
                }`}
                style={l.key === activeKey ? { backgroundColor: l.color } : {}}
              >
                <span className="font-en">{l.key}</span>
                <span className="hidden sm:inline">{l.name}</span>
              </button>
            ))}
          </div>
          <span
            data-testid="grammar-results-count"
            className="shrink-0 rounded-full bg-navy-900/6 px-3 py-1.5 text-xs font-bold text-navy-900/70"
          >
            {filtered.length} قاعدة
          </span>
        </div>
      </div>

      <div
        className="container-x pb-28 pt-8"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="grammar-swipe-area"
      >
        {isEmpty && (
          <div
            data-testid="grammar-empty-state"
            className="mx-auto max-w-xl rounded-3xl border border-dashed border-navy-900/20 bg-white/60 p-10 text-center"
          >
            <BookOpenText className="mx-auto mb-4 h-10 w-10 text-teal-700" />
            <p className="text-lg font-bold">دليل القواعد جاهز للتفعيل</p>
            <p className="mt-2 text-sm leading-loose text-navy-900/60">
              واجهة الدليل مكتملة (تبويبات، بحث فوري، عدّاد، وبطاقات القواعد)، وسيتم عرض الـ {GRAMMAR_TOTAL} قاعدة
              تلقائياً بمجرد إضافة ملف دليل القواعد (english-grammar-guide) إلى البيانات.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-500/10 px-4 py-2 text-xs font-bold text-teal-800">
              <RotateCw className="h-3.5 w-3.5" />
              المستويات: A1 • A2 • B1 • B2 • C1
            </p>
          </div>
        )}

        {!isEmpty && filtered.length === 0 && (
          <div className="mx-auto max-w-md rounded-2xl border border-dashed border-navy-900/20 bg-white/60 p-10 text-center">
            <SearchX className="mx-auto mb-3 h-8 w-8 text-navy-900/40" />
            <p className="font-bold">لا توجد نتائج مطابقة</p>
            <p className="mt-1 text-sm text-navy-900/55">جرّب كلمة أقصر أو مصطلحاً آخر.</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((rule, i) => (
            <RuleCard key={rule.id || i} rule={rule} level={level} index={i} />
          ))}
        </div>

        <p className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-navy-900/45">
          <ChevronLeft className="h-4 w-4" />
          اسحب يميناً أو يساراً للتبديل بين المستويات
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default GrammarPage;
